import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, 
  Shield,
  ShieldCheck,
  ShieldOff,
  Key,
  Smartphone,
  Copy,
  Check,
  AlertTriangle,
  Loader2
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

export default function SecuritySettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [backupCodesRemaining, setBackupCodesRemaining] = useState(0);
  
  // Setup state
  const [setupMode, setSetupMode] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  
  // Disable state
  const [disableMode, setDisableMode] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [disabling, setDisabling] = useState(false);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/2fa/status");
      setTwoFAEnabled(response.data.enabled);
      setBackupCodesRemaining(response.data.backup_codes_remaining || 0);
    } catch (error) {
      toast.error("Eroare la verificarea statusului 2FA");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const startSetup = async () => {
    try {
      setSetupMode(true);
      const response = await api.post("/admin/2fa/setup");
      setSetupData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la inițializarea 2FA");
      setSetupMode(false);
    }
  };

  const verifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("Introdu codul de 6 cifre din aplicația authenticator");
      return;
    }

    try {
      setVerifying(true);
      const response = await api.post("/admin/2fa/verify", { code: verificationCode });
      setBackupCodes(response.data.backup_codes || []);
      setShowBackupCodes(true);
      setTwoFAEnabled(true);
      toast.success("2FA activat cu succes!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cod invalid");
    } finally {
      setVerifying(false);
    }
  };

  const disable2FA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error("Introdu codul de 6 cifre pentru a dezactiva 2FA");
      return;
    }

    try {
      setDisabling(true);
      await api.post("/admin/2fa/disable", { code: disableCode });
      setTwoFAEnabled(false);
      setDisableMode(false);
      setDisableCode("");
      toast.success("2FA dezactivat");
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cod invalid");
    } finally {
      setDisabling(false);
    }
  };

  const regenerateBackupCodes = async () => {
    const code = prompt("Introdu codul din aplicația authenticator pentru a regenera codurile de backup:");
    if (!code) return;

    try {
      const response = await api.post("/admin/2fa/regenerate-backup-codes", { code });
      setBackupCodes(response.data.backup_codes || []);
      setShowBackupCodes(true);
      setBackupCodesRemaining(response.data.backup_codes.length);
      toast.success("Coduri de backup regenerate!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la regenerare");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
    toast.success("Copiat!");
  };

  const finishSetup = () => {
    setSetupMode(false);
    setSetupData(null);
    setVerificationCode("");
    setBackupCodes([]);
    setShowBackupCodes(false);
    fetchStatus();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#22c55e]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="bg-[#0f172a] border-b border-slate-800 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin")}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Înapoi
          </Button>
          <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-white">
            Security Settings
          </h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* 2FA Status Card */}
        <div className="bg-[#0f172a] border border-slate-800 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-4 mb-6">
            {twoFAEnabled ? (
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-green-500" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-amber-500" />
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-white">Two-Factor Authentication</h2>
              <p className={`text-sm ${twoFAEnabled ? "text-green-500" : "text-amber-500"}`}>
                {twoFAEnabled ? "Activat" : "Dezactivat"}
              </p>
            </div>
          </div>

          <p className="text-slate-400 mb-6">
            Autentificarea în doi pași adaugă un nivel suplimentar de securitate contului tău.
            Vei avea nevoie de telefonul tău pentru a te autentifica.
          </p>

          {!twoFAEnabled && !setupMode && (
            <Button
              onClick={startSetup}
              className="bg-[#22c55e] hover:bg-[#16a34a] text-black font-semibold"
            >
              <Smartphone className="w-4 h-4 mr-2" />
              Activează 2FA
            </Button>
          )}

          {twoFAEnabled && !disableMode && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Key className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-white font-medium">Coduri de backup</p>
                    <p className="text-slate-400 text-sm">{backupCodesRemaining} coduri rămase</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={regenerateBackupCodes}
                  className="border-slate-700 text-slate-400 hover:text-white"
                >
                  Regenerează
                </Button>
              </div>

              <Button
                variant="outline"
                onClick={() => setDisableMode(true)}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                <ShieldOff className="w-4 h-4 mr-2" />
                Dezactivează 2FA
              </Button>
            </div>
          )}
        </div>

        {/* Setup Flow */}
        {setupMode && setupData && !showBackupCodes && (
          <div className="bg-[#0f172a] border border-slate-800 rounded-lg p-6">
            <h3 className="text-lg font-bold text-white mb-4">Configurare 2FA</h3>
            
            <div className="space-y-6">
              {/* Step 1: QR Code */}
              <div>
                <p className="text-slate-400 mb-4">
                  <span className="text-white font-medium">Pasul 1:</span> Scanează codul QR cu aplicația ta authenticator 
                  (Google Authenticator, Microsoft Authenticator, Authy, etc.)
                </p>
                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <img 
                    src={`data:image/png;base64,${setupData.qr_code}`} 
                    alt="QR Code"
                    className="w-48 h-48"
                  />
                </div>
              </div>

              {/* Manual Entry */}
              <div>
                <p className="text-slate-400 mb-2">
                  <span className="text-white font-medium">Sau</span> introdu manual acest cod:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-slate-900 px-4 py-2 rounded text-[#22c55e] font-mono text-sm">
                    {setupData.manual_entry_key}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(setupData.manual_entry_key)}
                    className="text-slate-400 hover:text-white"
                  >
                    {copiedSecret ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Step 2: Verification */}
              <div>
                <p className="text-slate-400 mb-4">
                  <span className="text-white font-medium">Pasul 2:</span> Introdu codul de 6 cifre din aplicație pentru a verifica configurarea
                </p>
                <div className="flex gap-4">
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    className="flex-1 bg-slate-900 border border-slate-700 rounded px-4 py-3 text-white text-center text-2xl font-mono tracking-widest"
                    maxLength={6}
                  />
                  <Button
                    onClick={verifyAndEnable}
                    disabled={verifying || verificationCode.length !== 6}
                    className="bg-[#22c55e] hover:bg-[#16a34a] text-black font-semibold px-6"
                  >
                    {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verifică"}
                  </Button>
                </div>
              </div>

              <Button
                variant="ghost"
                onClick={() => { setSetupMode(false); setSetupData(null); }}
                className="text-slate-400"
              >
                Anulează
              </Button>
            </div>
          </div>
        )}

        {/* Backup Codes Display */}
        {showBackupCodes && backupCodes.length > 0 && (
          <div className="bg-[#0f172a] border border-amber-500/50 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              <h3 className="text-lg font-bold text-white">Salvează codurile de backup!</h3>
            </div>
            
            <p className="text-slate-400 mb-4">
              Aceste coduri pot fi folosite pentru a te autentifica dacă pierzi accesul la telefonul tău.
              <strong className="text-amber-400"> Salvează-le într-un loc sigur - nu le vei mai vedea!</strong>
            </p>

            <div className="grid grid-cols-2 gap-2 mb-6">
              {backupCodes.map((code, index) => (
                <div 
                  key={index}
                  className="bg-slate-900 px-4 py-2 rounded font-mono text-center text-white"
                >
                  {code}
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => copyToClipboard(backupCodes.join("\n"))}
                className="border-slate-700 text-slate-400 hover:text-white"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copiază toate
              </Button>
              <Button
                onClick={finishSetup}
                className="bg-[#22c55e] hover:bg-[#16a34a] text-black font-semibold"
              >
                Am salvat codurile
              </Button>
            </div>
          </div>
        )}

        {/* Disable 2FA Flow */}
        {disableMode && (
          <div className="bg-[#0f172a] border border-red-500/50 rounded-lg p-6">
            <h3 className="text-lg font-bold text-white mb-4">Dezactivare 2FA</h3>
            <p className="text-slate-400 mb-4">
              Pentru a dezactiva autentificarea în doi pași, introdu codul curent din aplicația authenticator.
            </p>
            
            <div className="flex gap-4 mb-4">
              <input
                type="text"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                className="flex-1 bg-slate-900 border border-slate-700 rounded px-4 py-3 text-white text-center text-2xl font-mono tracking-widest"
                maxLength={6}
              />
              <Button
                onClick={disable2FA}
                disabled={disabling || disableCode.length !== 6}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6"
              >
                {disabling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Dezactivează"}
              </Button>
            </div>

            <Button
              variant="ghost"
              onClick={() => { setDisableMode(false); setDisableCode(""); }}
              className="text-slate-400"
            >
              Anulează
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
